import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import CustomPagination from '../../../../components/forms/Pagination';
import { useTranslation } from 'react-i18next';
import { IconButton, SvgIcon, TableSortLabel, Typography, useTheme } from '@mui/material';
import { formatDateHourString, formatDateTime } from '../../../../utils';
import { Trash01 } from '../../../../assets';
import { SetStateAction, Dispatch, useState } from 'react';
import { EmptyTable } from '../../../../components/shared/EmptyTable';
import { Pill } from '../../../../components/shared/Pill';
import { ConfirmationDeleteDialog } from '../../../../components/shared/dialogs/ConfirmationDeleteDialog';
import { TableLoading } from '../../../../components/forms/TableLoading';
import { EmptyRows } from '../../../../components/forms/EmptyRows';
import { TableHeader } from '../../../../components/forms/TableHeader';
import { AutoshipScheduleModel, OrderType } from '../../../../app/services/api/orderManagementClient';
import { PaginatedList } from '../../../../app/services/api/orderManagementClient';
import { useNavigate } from 'react-router-dom';
import { AutoshipTemplateStatusEnum } from '../../enums/AutoshipmentTemplateStatusEnum';

export default function AutoshipmentScheduleTable(props: {
  readonly list: PaginatedList<AutoshipScheduleModel>,
  readonly page: number,
  readonly setPage: Dispatch<SetStateAction<number>>,
  readonly order: 'asc' | 'desc', 
  readonly setOrder: Dispatch<SetStateAction<'asc' | 'desc'>>,
  readonly orderBy: string,
  readonly setOrderBy: Dispatch<SetStateAction<string>>,
  readonly clearAll: () => void;
  readonly handleDelete: (id: number) => Promise<void>;
  readonly isLoading: boolean;
  readonly navigateToSchedule: (id: number) => void;
  readonly orderTypes: OrderType[];
  readonly hasWritePermissions: boolean;
  readonly hasDeletePermissions: boolean;
  readonly templateId: number;
  readonly templateStatus: number | undefined;
}) {
  const { t } = useTranslation('autoshipment');
  const theme = useTheme();
  const navigate = useNavigate();
  
  const rowsPerPage = 10;
  const maxPages = props.list?.totalCount
    ? Math.max(1, Math.ceil(props.list.totalCount / rowsPerPage))
    : 0;

  const [openModal, setOpenModal] = useState(false);
  const [deleteScheduleId, setDeleteScheduleId] = useState<number | null>(null);

  const deleteButtonHandler = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>, id: number) => {
    event.stopPropagation();
    setDeleteScheduleId(id);
    setOpenModal(true);
  };

  const handleConfirmDeleteModal = async () => {
    if (deleteScheduleId !== null) {
      await props.handleDelete(deleteScheduleId);
      setOpenModal(false);
      setDeleteScheduleId(null);
    }
  };

  const handleCancelModal = () => {
    setOpenModal(false);
    setDeleteScheduleId(null);
  };

  if (props.list.items?.length == 0) {
    return <EmptyTable bodyRowHeight={40}
      headerHeight={45}
      text={t('title')}
      searchTerm={''}
      onClear={props.clearAll}
      onAdd={() => navigate(`/manage-autoshipments/schedule/new/${props.templateId}`)}
      addItemText={t('newScheduleBtn')}
      onAddIsCreating={false}
      showOnAdd={props.templateStatus == Number(AutoshipTemplateStatusEnum.ACTIVE)}/>;
  }

  return (
    <>
      <TableHeader>
        {t('schedulesTitle')}
        {props.list?.totalCount > 0 && <Pill text={`${props.list?.totalCount} Schedule${props.list?.totalCount === 1 ? '' : 's'}`}></Pill>}
      </TableHeader>
      {props.isLoading ? <TableLoading headerHeight={45}
        bodyRowHeight={40}
        minTableWidth={480} /> :
        <TableContainer>
          <Table sx={{ minWidth: '480px' }}>
            <TableHead sx={{ backgroundColor: theme.palette.custom.gray[200] }}>
              <TableRow>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <TableSortLabel active={props.orderBy === 'name'}
                    direction={props.order}
                  >
                    {t('scheduleName')}
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <TableSortLabel active={props.orderBy === 'id'}
                    direction={props.order}
                  >
                    {t('scheduleId')}
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <TableSortLabel active={props.orderBy === 'instance'}
                    direction={props.order}
                  >
                    {t('instance')}
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <TableSortLabel active={props.orderBy === 'market'}
                    direction={props.order}
                  >
                    {t('market')}
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <TableSortLabel active={props.orderBy === 'orderType'}
                    direction={props.order}
                  >
                    {t('orderType')}
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <TableSortLabel active={props.orderBy === 'status'}
                    direction={props.order}
                  >
                    {t('status')}
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <TableSortLabel active={props.orderBy === 'itemCount'}
                    direction={props.order}
                  >
                    {t('itemCount')}
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <TableSortLabel active={props.orderBy === 'scheduleCount'}
                    direction={props.order}
                  >
                    {t('schedulesCreated')}
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <TableSortLabel active={props.orderBy === 'odsType'}
                    direction={props.order}
                  >
                    {t('ods')}
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <TableSortLabel active={props.orderBy === 'createDate'}
                    direction={props.order}
                  >
                    {t('created')}
                  </TableSortLabel>
                </TableCell>
                {props.hasWritePermissions && 
                <TableCell align="center"></TableCell>
                }
              </TableRow>
            </TableHead>
            <TableBody>
              {props.list?.items.map((row: AutoshipScheduleModel) => (
                <TableRow key={row.id}
                  onClick={() => props.navigateToSchedule(row.id!)}
                  sx={{ cursor: 'pointer' }}>
                  <TableCell>
                    <Typography variant='textXS'
                      fontWeight={600}
                      color={theme.palette.primary[500]}
                      sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                      {row.name ?? ''}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant='textXS' >
                      {row.id}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant='textXS' >
                      {row.company}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant='textXS' >
                      {row.customerType}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant='textXS' >
                      {
                        props.orderTypes.find(x => x.id === row.orderTypeId)?.name
                      }
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant='textXS' >
                      {t(`status-${row.statusId}`)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant='textXS' >
                      {row.itemCount}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant='textXS' >
                      {row.scheduleCount}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant='textXS' >
                      {t(`odstype-${row.odsTypeId}`)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant='textXS' >
                      {row.createDate ? formatDateTime(row.createDate!) : ''}
                    </Typography>
                  </TableCell>
                  {props.hasDeletePermissions ? (
                    row.statusId < 1 ? (
                      <TableCell
                        align="center"
                        sx={{ p: 0, alignContent: 'center' }}
                      >
                        <IconButton
                          size='small'
                          onClick={(event) => deleteButtonHandler(event, row.id ?? -1)}>
                          <SvgIcon style={{ color: theme.palette.error[500] }}><Trash01 /></SvgIcon>
                        </IconButton>
                      </TableCell>
                    ) : (
                      <TableCell />
                    )
                  ) : null}
                </TableRow>
              ))}

              <EmptyRows numberOfCurrentRows={props.list.items?.length ?? 0}
                height={40} />
            </TableBody>
          </Table>
        </TableContainer>}
      <CustomPagination useNoDataMessage={false}
        page={props.page}
        setPage={props.setPage}
        maxPages={maxPages}
        breakpointForChangeDisplay={120000}
        disabled={props.isLoading} />
      <ConfirmationDeleteDialog open={openModal}
        onCancel={handleCancelModal}
        onConfirm={handleConfirmDeleteModal}
        nameOfObject={t('scheduleLabel')} />
    </>
  );
}